'use client';
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { createClient } from '@/lib/supabase/client';
import { forgeGoalsService } from '@/lib/services/forgeGoalsService';

const WeightTrendChartInner = dynamic(() => import('./WeightTrendChartInner'), { ssr: false });

interface WeightDataPoint {
  day: string;
  actual?: number;
  trend?: number;
}

export default function WeightTrendChart() {
  const [chartData, setChartData] = useState<WeightDataPoint[]>([]);
  const [goalWeight, setGoalWeight] = useState<number | undefined>();

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;

      const profile = await forgeGoalsService.getUserProfile(user.id);
      if (profile?.target_weight_kg) setGoalWeight(profile.target_weight_kg);

      const { data: logs } = await supabase
        .from('forge_weight_logs')
        .select('weight_kg, log_date')
        .eq('user_id', user.id)
        .order('log_date', { ascending: true })
        .limit(60);

      if (!logs || logs.length === 0) {
        setChartData([]);
        return;
      }

      // Build chart data with 7-day moving average as trend
      const points: WeightDataPoint[] = logs.map((log, i) => {
        const window = logs.slice(Math.max(0, i - 6), i + 1);
        const avg = window.reduce((s, l) => s + l.weight_kg, 0) / window.length;
        const date = new Date(log.log_date);
        const label = date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
        return { day: label, actual: log.weight_kg, trend: Math.round(avg * 10) / 10 };
      });
      setChartData(points);
    });
  }, []);

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-forge-label">Weight Trend</div>
        <span className="text-2xs font-bold text-muted-foreground border border-border px-2 py-0.5 rounded">
          {chartData.length > 0 ? `${chartData.length} ENTRIES` : 'NO DATA'}
        </span>
      </div>
      <WeightTrendChartInner data={chartData} goalWeight={goalWeight} />
      {chartData.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
          <div>
            <div className="text-forge-label">Latest</div>
            <div className="font-tabular font-bold text-sm text-foreground">
              {chartData[chartData.length - 1]?.actual} kg
            </div>
          </div>
          <div>
            <div className="text-forge-label">7-Day Trend</div>
            <div className="font-tabular font-bold text-sm text-primary">
              {chartData[chartData.length - 1]?.trend} kg
            </div>
          </div>
        </div>
      )}
    </div>
  );
}